import jsPDF from "jspdf";

/**
 * Build a clean text-based PDF from candidate form data and return:
 *  - blob: the PDF blob (to upload as CV)
 *  - download(filename): triggers download in the browser
 *
 * Options:
 *  - anonymize: true → hides phone, whatsapp, email (for sharing with employers)
 */
export function generateResumePDF(data, options = {}) {
  const { anonymize = false } = options;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  const ensure = (need = 24) => {
    if (y + need > pageH - 40) {
      doc.addPage();
      y = margin;
    }
  };

  // Header band
  doc.setFillColor(21, 128, 61); // brand-700
  doc.rect(0, 0, pageW, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text(data.full_name || "Candidate", margin, 38);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(data.job_category || "", margin, 60);

  y = 110;
  doc.setTextColor(15, 23, 42);

  // Contact line — omit phone/email when anonymized
  const contactBits = anonymize
    ? [data.current_location && `Location: ${data.current_location}`]
    : [
        data.phone && `Phone: ${data.phone}`,
        data.whatsapp && `WhatsApp: ${data.whatsapp}`,
        data.email && `Email: ${data.email}`,
        data.current_location && `Location: ${data.current_location}`,
      ];
  const contact = contactBits.filter(Boolean).join("  •  ");
  doc.setFontSize(10);
  if (contact) {
    doc.text(contact, margin, y, { maxWidth: pageW - margin * 2 });
    y += 24;
  }
  if (anonymize) {
    doc.setTextColor(120, 53, 15); // amber-900 tone
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text(
      "Contact details withheld — please reach out via Tulsi Jobs Placement Consultant.",
      margin,
      y,
      { maxWidth: pageW - margin * 2 }
    );
    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 23, 42);
    y += 18;
  }

  const section = (title) => {
    ensure(36);
    doc.setDrawColor(21, 128, 61);
    doc.setLineWidth(1.2);
    doc.line(margin, y, pageW - margin, y);
    y += 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(21, 128, 61);
    doc.text(title.toUpperCase(), margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
  };

  const row = (label, value) => {
    if (!value) return;
    ensure(16);
    doc.setFont("helvetica", "bold");
    doc.text(label + ":", margin, y);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(String(value), pageW - margin * 2 - 130);
    doc.text(lines, margin + 130, y);
    y += 14 * lines.length;
  };

  const paragraph = (text) => {
    if (!text) return;
    const lines = doc.splitTextToSize(String(text), pageW - margin * 2);
    ensure(14 * lines.length);
    doc.text(lines, margin, y);
    y += 14 * lines.length + 6;
  };

  if (data.career_objective || data.summary) {
    section("Career Objective");
    paragraph(data.career_objective || data.summary);
  }

  section("Personal");
  row("Father's Name", data.father_name);
  row("Date of Birth", data.date_of_birth);
  row("Age", data.age ? `${data.age} years` : "");
  row("Gender", data.gender);
  row("Marital Status", data.marital_status);
  row(
    "Address",
    [data.address, data.city, data.state, data.pincode].filter(Boolean).join(", ")
  );

  // Education year-wise
  const eduRows = [
    ["10th", data.edu_10th],
    ["12th", data.edu_12th],
    ["Graduation", data.edu_graduation],
    ["Post-Graduation", data.edu_post_graduation],
  ].filter(([, e]) => e && (e.school || e.board || e.year || e.percentage));
  if (eduRows.length || data.highest_qualification) {
    section("Education");
    eduRows.forEach(([level, e]) => {
      const parts = [
        e.school && `${e.school}`,
        e.board && `(${e.board})`,
        e.year && `Year: ${e.year}`,
        e.percentage && `${e.percentage}`,
      ]
        .filter(Boolean)
        .join("  ");
      row(level, parts);
    });
    if (data.highest_qualification && !eduRows.length) {
      row("Highest Qualification", data.highest_qualification);
      row("Institute", data.institute);
      row("Year", data.passing_year);
      row("Percentage / CGPA", data.percentage);
    }
  }

  section("Experience");
  row(
    "Total Experience",
    data.total_experience != null ? `${data.total_experience} years` : ""
  );
  row("Current Company", data.current_company);
  row("Current Designation", data.current_designation);
  row(
    "Current Salary",
    data.current_salary ? `INR ${Number(data.current_salary).toLocaleString("en-IN")} / month` : ""
  );
  row(
    "Expected Salary",
    data.expected_salary ? `INR ${Number(data.expected_salary).toLocaleString("en-IN")} / month` : ""
  );
  row("Notice Period", data.notice_period);

  const prev = (data.previous_companies || []).filter((c) => c && (c.company || c.designation));
  if (prev.length) {
    section("Previous Employment");
    prev.forEach((c, i) => {
      ensure(40);
      doc.setFont("helvetica", "bold");
      doc.text(`${i + 1}. ${c.company || ""}`, margin, y);
      y += 13;
      doc.setFont("helvetica", "normal");
      const sub = [c.designation, c.duration].filter(Boolean).join(" · ");
      if (sub) {
        doc.text(sub, margin, y);
        y += 13;
      }
      if (c.role) {
        const lines = doc.splitTextToSize(c.role, pageW - margin * 2);
        ensure(14 * lines.length);
        doc.text(lines, margin, y);
        y += 13 * lines.length;
      }
      y += 4;
    });
  }

  if (data.skills?.length) {
    section("Skills");
    paragraph(data.skills.join(" • "));
  }
  if (data.certifications?.length) {
    section("Certifications");
    paragraph(data.certifications.join(" • "));
  }
  if (data.languages?.length) {
    section("Languages");
    paragraph(data.languages.join(" • "));
  }
  if (data.english_level) {
    section("English Communication");
    paragraph(data.english_level);
  }

  // Footer
  doc.setDrawColor(200);
  doc.setFontSize(8);
  doc.setTextColor(100);
  const footerText = anonymize
    ? "Employer Copy · Contact via Tulsi Jobs Placement Consultant"
    : "Generated via Tulsi Jobs Placement Consultant";
  doc.text(footerText, pageW / 2, pageH - 20, { align: "center" });

  const blob = doc.output("blob");
  return {
    blob,
    download(filename = "resume.pdf") {
      doc.save(filename);
    },
  };
}
